import React, { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, Pencil, Plus, Search, Trash2, X, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

const err = (e, fb) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || fb);

function AddForm({ onDone, onCancel }) {
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff", { nombre, email: email || null });
      toast.success("Empleado añadido");
      onDone();
    } catch (ex) { err(ex, "No se pudo añadir"); }
  };
  return (
    <form onSubmit={save} className="card p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="staff-add-form">
      <label className="text-xs">Nombre<input className="input mt-1 block" value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid="staff-add-nombre" /></label>
      <label className="text-xs">Email<input type="email" className="input mt-1 block" style={{ width: 260 }} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="staff-add-email" /></label>
      <button type="submit" disabled={!nombre.trim()} className="btn btn-primary" data-testid="staff-add-save">Guardar</button>
      <button type="button" onClick={onCancel} className="btn btn-outline" data-testid="staff-add-cancel">Cancelar</button>
    </form>
  );
}

function Row({ p, onChange }) {
  const [edit, setEdit] = useState(false);
  const [nombre, setNombre] = useState(p.nombre);
  const [email, setEmail] = useState(p.email || "");
  const call = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); setEdit(false); onChange(); } catch (ex) { err(ex, "No se pudo guardar"); }
  };
  const save = () => call(() => api.put(`/staff/${p.id}`, email ? { nombre, email } : { nombre, clear_email: true }), "Guardado");
  const pend = p.estado === "pendiente";
  return (
    <tr data-testid={`staff-row-${p.vendedor}`} className={p.activo ? "" : "opacity-50"}>
      <td className="font-semibold">
        {edit ? <input className="input text-sm py-1" value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid={`staff-edit-nombre-${p.vendedor}`} />
          : <>{p.nombre}{p.nombre !== p.vendedor && <span className="text-[10px] text-slate-400 font-normal ml-1">({p.vendedor})</span>}</>}
      </td>
      <td>
        {edit ? <input type="email" className="input text-sm py-1" style={{ width: 240 }} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@..." data-testid={`staff-edit-email-${p.vendedor}`} />
          : p.email ? <span className="font-mono text-xs">{p.email}</span>
            : <button onClick={() => setEdit(true)} className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors" data-testid={`staff-add-email-${p.vendedor}`}>+ Añadir email</button>}
      </td>
      <td>
        {pend ? <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-amber-100 text-amber-800"><AlertCircle size={11} /> Pendiente revisar</span>
          : <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-emerald-100 text-emerald-800"><CheckCircle2 size={11} /> Confirmado</span>}
      </td>
      <td>
        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={p.activo} onChange={(e) => call(() => api.put(`/staff/${p.id}`, { activo: e.target.checked }))} data-testid={`staff-activo-${p.vendedor}`} />
          {p.activo ? "Sí" : "No"}
        </label>
      </td>
      <td className="text-right whitespace-nowrap">
        {edit ? (
          <>
            <button onClick={save} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" title="Guardar" data-testid={`staff-save-${p.vendedor}`}><Check size={15} /></button>
            <button onClick={() => { setEdit(false); setNombre(p.nombre); setEmail(p.email || ""); }} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded" title="Cancelar"><X size={15} /></button>
          </>
        ) : (
          <>
            {pend && <button onClick={() => call(() => api.post(`/staff/${p.id}/confirm`), "Confirmado")} className="text-[11px] font-bold uppercase px-3 py-1 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-colors mr-1" data-testid={`staff-confirm-${p.vendedor}`}>Confirmar</button>}
            <button onClick={() => setEdit(true)} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded" title="Editar" data-testid={`staff-edit-${p.vendedor}`}><Pencil size={15} /></button>
            <button onClick={() => window.confirm(`¿Eliminar a ${p.nombre} del personal?`) && call(() => api.delete(`/staff/${p.id}`), "Eliminado")} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Eliminar" data-testid={`staff-delete-${p.vendedor}`}><Trash2 size={15} /></button>
          </>
        )}
      </td>
    </tr>
  );
}

export const PersonalTab = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/staff").then((r) => setData(r.data)).catch(() => setData({ personal: [], copia_titular: false }));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Cargando personal…</div>;
  const pend = data.personal.filter((p) => p.estado === "pendiente").length;
  const list = data.personal.filter((p) => p.nombre.toLowerCase().includes(q.toLowerCase()));
  const toggleCopia = async (v) => {
    try { await api.put("/staff/settings", { copia_titular: v }); setData({ ...data, copia_titular: v }); } catch (ex) { err(ex, "No se pudo guardar"); }
  };
  return (
    <div data-testid="personal-tab">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <p className="text-sm text-slate-600 max-w-2xl">Los empleados que aparecen en los ficheros de ventas se añaden aquí como pendientes de revisar. Confírmalos, corrige el nombre, añade su email y elige quién entra en los análisis (ranking, ficha e incentivo grupal).</p>
        <button onClick={() => setAdding(true)} className="btn btn-primary" data-testid="staff-add-btn"><Plus size={15} /> Añadir empleado</button>
      </div>
      {adding && <AddForm onDone={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />}
      {pend > 0 && (
        <div className="card p-4 mb-4 border-amber-300 bg-amber-50 text-sm text-amber-900 flex gap-2" data-testid="staff-pending-banner">
          <AlertCircle size={17} className="shrink-0 mt-0.5" /><span><b>{pend}</b> empleado{pend > 1 ? "s" : ""} pendiente{pend > 1 ? "s" : ""} de confirmar. Se crearon automáticamente al subir las ventas. Revisa los nombres y confírmalos.</span>
        </div>
      )}
      <label className="card p-4 mb-4 flex gap-3 cursor-pointer" data-testid="staff-copia-titular">
        <input type="checkbox" className="mt-1" checked={data.copia_titular} onChange={(e) => toggleCopia(e.target.checked)} data-testid="staff-copia-checkbox" />
        <span className="text-sm"><b>Recibir también yo (titular) los informes que se envíen a los empleados</b>
          <span className="block text-xs text-slate-500">Cada vez que envíes su informe por email a un empleado, te llegará una copia a tu email de acceso con el asunto «[Copia titular]».</span></span>
      </label>
      <div className="card overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <Search size={15} className="text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre..." className="flex-1 outline-none text-sm bg-transparent" data-testid="staff-search" />
          <span className="text-xs text-slate-500">{list.length} de {data.personal.length}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="dense" data-testid="staff-table">
            <thead><tr><th>Nombre</th><th>Email</th><th>Estado</th><th>En análisis</th><th /></tr></thead>
            <tbody>{list.map((p) => <Row key={p.id} p={p} onChange={load} />)}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
